import apiClient, { handleApiError } from '../../utils/apiClient';
import type {
  DoctorShiftRule,
  DoctorShiftRuleGrouped,
  RuleType,
  TimeCondition
} from '../../domain/doctorShiftRule';

/**
 * APIレスポンス型定義
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/**
 * API一覧レスポンス型
 */
interface DoctorShiftRuleListApiResponse {
  data: DoctorShiftRuleGrouped[];
  meta: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
  };
}

/**
 * API作成リクエスト型
 */
interface CreateDoctorShiftRuleApiRequest {
  doctor_ids: string[];
  rule_type: RuleType;
  time_conditions: Array<{
    week_number: number | 'all';
    day_of_week: string;
    start_time: string;
    end_time: string;
  }>;
}

/**
 * API更新リクエスト型
 */
interface UpdateDoctorShiftRuleApiRequest {
  rule_type?: RuleType;
  time_conditions?: Array<{
    week_number: number | 'all';
    day_of_week: string;
    start_time: string;
    end_time: string;
  }>;
}

/**
 * TimeConditionをAPIフォーマット（snake_case）に変換
 * @param condition TimeCondition フロントエンド形式の時間条件
 * @returns object API形式の時間条件（snake_case）
 */
function toApiTimeCondition(condition: TimeCondition): {
  week_number: number | 'all';
  day_of_week: string;
  start_time: string;
  end_time: string;
} {
  return {
    week_number: condition.weekNumber,
    day_of_week: condition.dayOfWeek,
    start_time: condition.startTime,
    end_time: condition.endTime
  };
}

/**
 * 医師シフトルールAPI（バックエンドAPIとの通信を担当）
 */
export const doctorShiftRuleApi = {
  /**
   * シフトルール一覧取得
   * @param params object 検索パラメータ（page, page_size, search, doctor_id, rule_type）
   * @returns Promise<DoctorShiftRuleListApiResponse> 一覧取得結果
   */
  getAll: async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    doctor_id?: string;
    rule_type?: RuleType;
  }): Promise<DoctorShiftRuleListApiResponse> => {
    try {
      const response = await apiClient.get<ApiResponse<DoctorShiftRuleListApiResponse>>(
        '/api/doctor-shift-rules',
        { params }
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '一覧取得に失敗しました');
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * シフトルール詳細取得
   * @param id string シフトルールID
   * @returns Promise<DoctorShiftRule> シフトルール詳細
   */
  getById: async (id: string): Promise<DoctorShiftRule> => {
    try {
      const response = await apiClient.get<ApiResponse<DoctorShiftRule>>(
        `/api/doctor-shift-rules/${id}`
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '詳細取得に失敗しました');
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * シフトルール作成
   * @param data object 作成データ（doctorIds, ruleType, timeConditions）
   * @returns Promise<DoctorShiftRule[]> 作成されたシフトルール配列
   */
  create: async (data: {
    doctorIds: string[];
    ruleType: RuleType;
    timeConditions: TimeCondition[];
  }): Promise<DoctorShiftRule[]> => {
    try {
      const request: CreateDoctorShiftRuleApiRequest = {
        doctor_ids: data.doctorIds,
        rule_type: data.ruleType,
        time_conditions: data.timeConditions.map(toApiTimeCondition)
      };

      const response = await apiClient.post<ApiResponse<DoctorShiftRule[]>>(
        '/api/doctor-shift-rules',
        request
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '作成に失敗しました');
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * シフトルール更新
   * @param id string シフトルールID
   * @param data object 更新データ（ruleType, timeConditions）
   * @returns Promise<DoctorShiftRule> 更新されたシフトルール
   */
  update: async (id: string, data: {
    ruleType?: RuleType;
    timeConditions?: TimeCondition[];
  }): Promise<DoctorShiftRule> => {
    try {
      const request: UpdateDoctorShiftRuleApiRequest = {};
      if (data.ruleType !== undefined) {
        request.rule_type = data.ruleType;
      }
      if (data.timeConditions !== undefined) {
        request.time_conditions = data.timeConditions.map(toApiTimeCondition);
      }

      const response = await apiClient.put<ApiResponse<DoctorShiftRule>>(
        `/api/doctor-shift-rules/${id}`,
        request
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '更新に失敗しました');
      }

      return response.data.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * シフトルール削除（論理削除）
   * @param id string シフトルールID
   * @returns Promise<void> なし
   */
  delete: async (id: string): Promise<void> => {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        `/api/doctor-shift-rules/${id}`
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.message || '削除に失敗しました');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }
};

export default doctorShiftRuleApi;
